import React, { useState, useEffect } from 'react';
import { leaderboardService } from '../services/leaderboardService';
import { PencilIcon, TrashIcon, ArrowDownTrayIcon, ExclamationTriangleIcon, ArrowTopRightOnSquareIcon, EnvelopeIcon, CheckCircleIcon, GiftIcon } from '@heroicons/react/24/outline';
import ModalBasic from '../components/ModalBasic';
import { motion, AnimatePresence } from 'framer-motion';
import { normalizeCampaignStatus } from '../data/campaignStatus';

const ManageLeaderboard = ({ campaignId, campaign, hideOpenDisplay = false, onLoadTotals }) => {
  const [entries, setEntries] = useState([]);
  const [totalEntries, setTotalEntries] = useState(0);
  const [emailCount, setEmailCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  
  useEffect(() => {
    let cancelled = false;
    const timeoutId = setTimeout(() => {
      if (!cancelled) setIsLoading(false);
    }, 12000); // Stop showing loading after 12s so mobile never hangs indefinitely
    loadEntries();
    loadTotalEntries();
    loadEmailCount();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [campaignId]);

  useEffect(() => {
    if (showDeleteModal) {
      const timer = setTimeout(() => {
        setDeleteModalOpen(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showDeleteModal]);

  useEffect(() => {
    if (showEditModal) {
      const timer = setTimeout(() => {
        setEditModalOpen(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showEditModal]);

  useEffect(() => {
    if (onLoadTotals) {
      onLoadTotals({ totalEntries, emailCount });
    }
  }, [totalEntries, emailCount, onLoadTotals]);

  const loadEntries = async () => {
    setIsLoading(true);
    try {
      // Entries doc no longer carries email / marketingConsent (moved to
      // the auth-gated contactsPrivate sibling). Fetch top entries first,
      // then join ONLY the visible entries' contacts (bounded to 10
      // doc reads) — never read the whole contactsPrivate collection here.
      const { entries: rawEntries } = await leaderboardService.getTopScores(campaignId, 10);
      const ids = rawEntries.map(e => e.id);
      const contacts = await leaderboardService.getContactsByIds(campaignId, ids);
      const merged = rawEntries.map(e => {
        const c = contacts.get(e.id) || {};
        return {
          ...e,
          email: c.email || e.email || '',
          marketingConsent: c.marketingConsent ?? e.marketingConsent ?? false,
        };
      });
      setEntries(merged);
    } catch (error) {
      console.error('Error loading leaderboard entries:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTotalEntries = async () => {
    try {
      const count = await leaderboardService.getTotalEntries(campaignId);
      setTotalEntries(count);
    } catch (error) {
      console.error('Error loading total leaderboard entries:', error);
    }
  };

  const loadEmailCount = async () => {
    try {
      const count = await leaderboardService.getEmailCount(campaignId);
      setEmailCount(count);
    } catch (error) {
      console.error('Error loading email count:', error);
    }
  };

  const handleDelete = async () => {
    if (!entryToDelete) return;
    
    try {
      await leaderboardService.deleteEntry(campaignId, entryToDelete);
      setDeleteModalOpen(false);
      setShowDeleteModal(false);
      setEntryToDelete(null);
      // Parallel: refresh entries + both counts in one round-trip rather
      // than three serial fetches blocking the modal close.
      await Promise.all([loadEntries(), loadTotalEntries(), loadEmailCount()]);
    } catch (error) {
      console.error('Error deleting entry:', error);
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setTimeout(() => {
      setShowDeleteModal(false);
      setEntryToDelete(null);
    }, 300);
  };

  // Allow letters (any language), numbers, space, hyphen, apostrophe for display names
  const sanitizeName = (value) => value.replace(/[^\p{L}\p{N}\s\-']/gu, '');

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setEditName((entry.name || '').trim());
    setEditEmail((entry.email || '').trim());
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setTimeout(() => {
      setShowEditModal(false);
      setEditingEntry(null);
      setEditName('');
      setEditEmail('');
    }, 300);
  };

  const handleSave = async () => {
    if (!editingEntry) return;
    const trimmedName = editName.trim();
    try {
      // Email is intentionally NOT passed: it lives on the auth-gated
      // contactsPrivate collection which clients can't write. Admin email
      // edits need a dedicated Cloud Function (TODO). The field is read-only
      // in the UI for now.
      await leaderboardService.updateScore(campaignId, editingEntry.id, {
        name: sanitizeName(trimmedName),
      });
      // Counts don't change on a name edit; only refresh the list.
      await loadEntries();

      handleCloseEditModal();
    } catch (error) {
      console.error('Error updating entry:', error);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      let allEntries = [];
      let lastDoc = null;
      const batchSize = 100; // Fetch 100 entries at a time
      const maxEntries = 5000; // Safety cap to prevent runaway costs

      console.log('📥 Starting paginated download...');

      // Fetch entries (public leaderboard data) and contactsPrivate (emails
      // + consent) in parallel so the CSV can join them by entryId. Without
      // this merge every row would show "No email provided" since email
      // no longer lives on entries docs.
      const contactsPromise = leaderboardService.getAllContactsForExport(campaignId);

      // Fetch in batches to avoid timeout and manage costs
      do {
        console.log(`📦 Fetching batch... Current total: ${allEntries.length}`);

        const { entries, lastVisible } = await leaderboardService.getTopScores(
          campaignId,
          batchSize,
          lastDoc
        );

        allEntries = [...allEntries, ...entries];
        lastDoc = lastVisible;

        // Break if we hit our safety cap
        if (allEntries.length >= maxEntries) {
          console.warn(`⚠️ Download capped at ${maxEntries} entries for cost safety`);
          break;
        }

        // Break if we got fewer entries than requested (end of data)
      } while (lastDoc && entries.length === batchSize);

      const contactsMap = await contactsPromise;
      allEntries = allEntries.map(e => {
        const c = contactsMap.get(e.id) || {};
        return {
          ...e,
          email: c.email || e.email || '',
          marketingConsent: c.marketingConsent ?? e.marketingConsent ?? false,
        };
      });

      console.log(`✅ Download complete: ${allEntries.length} entries fetched`);
      
      if (allEntries.length === 0) {
        console.warn('No entries found for download');
        return;
      }
      
      // Convert entries to CSV format
      const headers = ['Rank', 'Name', 'Email', 'Marketing Consent', 'Score', 'Date', 'Time', 'Flagged', 'Suspicious', 'Validation Issues'];
      const csvData = allEntries.map((entry, index) => [
        index + 1,
        entry.name || 'Unknown',
        entry.email || 'No email provided',
        entry.marketingConsent ? 'Yes' : 'No',
        entry.score || 0,
        entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : 'Unknown',
        entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : 'Unknown',
        entry.flagged ? 'Yes' : 'No',
        entry.suspicious ? 'Yes' : 'No',
        (entry.suspiciousReasons || []).concat((entry.validationIssues || []).map(i => i.reason || i.type)).join('; ') || 'None'
      ]);
      
      // Add headers to the beginning
      csvData.unshift(headers);
      
      // Convert to CSV string
      const csvString = csvData
        .map(row => row.map(cell => 
          cell.toString().includes(',') ? `"${cell}"` : cell
        ).join(','))
        .join('\n');
      
      // Create blob and download
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      // Use internal name for filename, fallback to title, then campaignId
      const gameName = campaign?.internalName || campaign?.options?.title || campaignId;
      // Sanitize filename: remove special characters, replace spaces with dashes
      const sanitizedName = gameName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-').substring(0, 50);
      const fileName = `${sanitizedName}_leaderboard_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      URL.revokeObjectURL(url);
      
      console.log(`📁 CSV downloaded: ${fileName} (${allEntries.length} entries)`);
      
    } catch (error) {
      console.error('Error downloading leaderboard:', error);
      // You could add a toast notification here
    } finally {
      setIsDownloading(false);
    }
  };



  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="gch-card overflow-hidden"
    >
      <header className="px-5 py-4 border-b border-white/10">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-xs font-semibold uppercase tracking-wide gch-text-secondary">
              Leaderboard
            </h2>
            <p className="text-xs gch-text-secondary mt-1 flex items-center gap-3 flex-wrap tabular-nums">
              {totalEntries.toLocaleString()} players · {emailCount.toLocaleString()} leads
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!hideOpenDisplay && (
              <a
                href={`/leaderboard/${campaignId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-admin-secondary btn-admin-sm inline-flex items-center gap-1.5"
                title="Open leaderboard display in a new tab (for TV or kiosk)"
                aria-label="Open leaderboard display in new tab"
              >
                <ArrowTopRightOnSquareIcon className="w-4 h-4 shrink-0" aria-hidden />
                Live leaderboard
              </a>
            )}
            {!hideOpenDisplay && (
              totalEntries >= 5 ? (
                <a
                  href={`/leaderboard/${campaignId}?pick=1`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-admin-primary btn-admin-sm inline-flex items-center gap-1.5"
                  title="Open the live leaderboard and pick a random winner from the full entry list"
                  aria-label="Pick a random winner from the full entry list"
                >
                  <GiftIcon className="w-4 h-4 shrink-0" aria-hidden />
                  Pick winner
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  className="btn-admin-secondary btn-admin-sm inline-flex items-center gap-1.5 opacity-40 cursor-not-allowed"
                  title={`Need at least 5 entries to pick a winner (${totalEntries}/5)`}
                  aria-label="Need at least 5 entries to pick a winner"
                >
                  <GiftIcon className="w-4 h-4 shrink-0" aria-hidden />
                  Pick winner
                </button>
              )
            )}
            {/* Flagged count badge */}
            {entries.filter(e => e.flagged || e.suspicious).length > 0 && (
              <div className="flex items-center gap-2 px-2.5 py-1 rounded-full bg-red-500/20 border border-red-400/30">
                <ExclamationTriangleIcon className="w-4 h-4 text-red-400" />
                <span className="text-sm font-semibold text-red-300">
                  {entries.filter(e => e.flagged || e.suspicious).length} flagged
                </span>
              </div>
            )}
          </div>
        </div>
      </header>
      
      {/* Leaderboard Entries  -  full-width rows so hover spans edge to edge */}
      <div className="pt-0 pb-0">
        <AnimatePresence>
          {isLoading ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-5 px-5 gch-text-secondary"
            >
              <div className="text-sm font-semibold">Loading data...</div>
            </motion.div>
          ) : entries.length === 0 ? (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mx-4 my-3 text-center rounded-lg gch-input-ghost px-4 py-3"
            >
              <p className="text-xs gch-text-muted">No participants yet</p>
            </motion.div>
          ) : (
            entries.map((entry, index) => {
              const rank = index + 1;
              const isFlagged = entry.flagged || entry.suspicious;
              const validationIssues = entry.validationIssues || [];
              const suspiciousReasons = entry.suspiciousReasons || [];
              
              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleEdit(entry)}
                  className={`flex items-center justify-between gap-4 px-5 py-1.5 cursor-pointer ${
                    isFlagged 
                      ? 'bg-red-500/10 hover:bg-red-500/20' 
                      : 'hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className={`text-sm font-medium w-7 shrink-0 ${
                      isFlagged ? 'text-red-400' : 'gch-text-secondary'
                    }`}>
                      {rank}.
                    </div>
                    
                    {/* Player Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`font-medium text-xs ${
                          isFlagged ? 'text-red-200' : 'gch-text-primary'
                        }`}>
                          {entry.name}
                        </span>
                        {/* Entry options: email, consent & flagged icons on same line */}
                        {entry.email ? (
                          <span className="inline-flex items-center" title="Email captured">
                            <EnvelopeIcon className="w-3 h-3 gch-text-secondary shrink-0" aria-hidden />
                          </span>
                        ) : (
                          <span className="inline-flex items-center" title="No email provided">
                            <EnvelopeIcon className="w-3 h-3 gch-text-muted opacity-50 shrink-0" aria-hidden />
                          </span>
                        )}
                        {entry.marketingConsent ? (
                          <span className="inline-flex items-center" title="Marketing consent given">
                            <CheckCircleIcon className="w-3 h-3 text-emerald-400/90 shrink-0" aria-hidden />
                          </span>
                        ) : (
                          <span className="inline-flex items-center" title="No marketing consent">
                            <CheckCircleIcon className="w-3 h-3 gch-text-muted opacity-40 shrink-0" aria-hidden />
                          </span>
                        )}
                        {isFlagged && (
                          <span
                            className="inline-flex items-center shrink-0"
                            title={suspiciousReasons[0] || validationIssues[0]?.reason || 'Flagged – suspicious activity or validation issue'}
                          >
                            <ExclamationTriangleIcon className="w-3 h-3 text-amber-400" aria-hidden />
                          </span>
                        )}
                      </div>
                      {entry.email ? (
                        <div className={`text-xs mt-0.5 ${isFlagged ? 'text-red-400' : 'gch-text-secondary'}`} style={{fontSize: '10px'}}>
                          {entry.email}
                        </div>
                      ) : null}
                      {/* Show validation issues for flagged entries */}
                      {isFlagged && (suspiciousReasons.length > 0 || validationIssues.length > 0) && (
                        <div className="mt-1.5">
                          <div className="text-sm text-red-700 font-medium">
                            {suspiciousReasons[0] || validationIssues[0]?.reason || 'Suspicious score detected'}
                          </div>
                          {(suspiciousReasons.length > 1 || validationIssues.length > 1) && (
                            <div className="text-sm text-red-600 mt-0.5">
                              +{suspiciousReasons.length + validationIssues.length - 1} more issue{((suspiciousReasons.length + validationIssues.length - 1) !== 1) ? 's' : ''}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Score */}
                    <div className="text-right shrink-0">
                      <div className={`font-semibold text-sm tabular-nums ${
                        isFlagged ? 'text-red-200' : 'gch-text-primary'
                      }`}>
                        {entry.score.toLocaleString()}
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEntryToDelete(entry.id);
                        setShowDeleteModal(true);
                      }}
                      className={`p-2 rounded-[var(--admin-card-radius)] transition-all duration-200 ${
                        isFlagged
                          ? 'text-red-400/80 hover:text-red-400 hover:bg-red-500/10 border border-red-400/20'
                          : 'text-white/40 hover:text-red-400/90 hover:bg-red-500/10'
                      }`}
                      title={isFlagged ? 'Delete flagged score' : 'Delete entry'}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </motion.button>
                  </div>
                </motion.div>
              );
            })
          )}
        </AnimatePresence>
      </div>

      {/* Download Button - Only show when there are entries */}
      {entries.length > 0 && (
        <div className="mx-5 mt-1.5 pt-2 pb-3 border-t border-white/10">
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className={`w-full flex items-center justify-center gap-2 text-xs transition-all ${
              isDownloading
                ? 'gch-text-muted cursor-not-allowed' 
                : 'gch-text-secondary hover:gch-text-primary'
            }`}
          >
            {isDownloading ? (
              <span className="animate-pulse">Downloading...</span>
            ) : (
              <>
                <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                <span>Export to CSV</span>
              </>
            )}
          </button>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingEntry && (
        <ModalBasic
          id="edit-participant-modal"
          modalOpen={editModalOpen}
          setModalOpen={handleCloseEditModal}
          title="Participant Details"
        >
          <div className="p-6">
            <div className="space-y-4">
              {/* Name Input */}
              <div>
                <label className="block text-sm font-semibold mb-2 gch-text-primary">
                  Name
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(sanitizeName(e.target.value))}
                  onBlur={() => setEditName((v) => v.trim())}
                  className="w-full px-3 py-2 rounded-lg font-medium gch-input-ghost gch-text-primary border border-[var(--admin-border)] focus:border-[var(--admin-accent)] focus:ring-2 focus:ring-[var(--admin-accent-ring)]"
                  autoFocus
                />
              </div>
              
              {/* Email Display */}
              <div>
                <label className="block text-sm font-semibold mb-2 gch-text-primary">
                  Email
                </label>
                <div className="px-3 py-2 rounded-lg text-sm gch-input-ghost border border-[var(--admin-border)] gch-text-secondary">
                  {editEmail || 'No email provided'}
                </div>
              </div>

              {/* Two column layout for smaller fields */}
              <div className="grid grid-cols-2 gap-4">
                {/* Score */}
                <div>
                  <label className="block text-sm font-semibold mb-2 gch-text-primary">
                    Score
                  </label>
                  <div className="px-3 py-2 rounded-lg text-sm gch-input-ghost border border-[var(--admin-border)] gch-text-primary font-semibold">
                    {editingEntry.score?.toLocaleString() || '0'}
                  </div>
                </div>

                {/* Marketing Consent */}
                <div>
                  <label className="block text-sm font-semibold mb-2 gch-text-primary">
                    Marketing Consent
                  </label>
                  <div className={`px-3 py-2 rounded-lg text-sm border ${
                    editingEntry.marketingConsent 
                      ? 'bg-green-500/10 border-green-400/30 text-green-400' 
                      : 'gch-input-ghost border-[var(--admin-border)] gch-text-secondary'
                  }`}>
                    {editingEntry.marketingConsent ? 'Yes' : 'No'}
                  </div>
                </div>
              </div>

              {/* Date/Time */}
              <div>
                <label className="block text-sm font-semibold mb-2 gch-text-primary">
                  Submitted
                </label>
                <div className="px-3 py-2 rounded-lg text-sm gch-input-ghost border border-[var(--admin-border)] gch-text-secondary">
                  {editingEntry.timestamp 
                    ? `${new Date(editingEntry.timestamp).toLocaleDateString()} at ${new Date(editingEntry.timestamp).toLocaleTimeString()}`
                    : 'Unknown'
                  }
                </div>
              </div>

              {/* Flagged Status - Only show if flagged */}
              {(editingEntry.flagged || editingEntry.suspicious) && (
                <div>
                  <label className="block text-sm font-semibold mb-2 text-red-400">
                    Validation Issues
                  </label>
                  <div className="px-3 py-2 rounded-lg text-sm bg-red-500/10 border border-red-400/30">
                    <ul className="space-y-1 text-red-300">
                      {(editingEntry.suspiciousReasons || []).map((reason, idx) => (
                        <li key={idx} className="flex items-start gap-1.5">
                          <span className="text-red-400">•</span>
                          <span>{reason}</span>
                        </li>
                      ))}
                      {(editingEntry.validationIssues || []).map((issue, idx) => (
                        <li key={`issue-${idx}`} className="flex items-start gap-1.5">
                          <span className="text-red-400">•</span>
                          <span>{issue.reason || issue.type}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={handleCloseEditModal}
                  className="btn-admin-outline btn-admin-md"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="btn-admin-secondary btn-admin-md"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </ModalBasic>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (() => {
        const entryToDeleteData = entries.find(e => e.id === entryToDelete);
        const isFlaggedEntry = entryToDeleteData?.flagged || entryToDeleteData?.suspicious;
        
        return (
          <ModalBasic
            id="delete-confirmation-modal"
            modalOpen={deleteModalOpen}
            setModalOpen={handleCloseDeleteModal}
            title={isFlaggedEntry ? "Delete Flagged Score" : "Confirm Removal"}
          >
            <div className="p-6">
              <div className="mb-6 text-center">
                <div className="flex justify-center mb-3">
                  <ExclamationTriangleIcon className={`w-12 h-12 ${isFlaggedEntry ? 'text-red-400' : 'text-red-400'}`} />
                </div>
                <p className="font-semibold gch-text-primary">
                  {isFlaggedEntry 
                    ? 'Are you sure you want to delete this flagged score?'
                    : 'Are you sure you want to remove this participant from the leaderboard?'}
                </p>
                <p className="text-sm mt-2 gch-text-secondary">
                  This action cannot be undone!
                </p>
                {/* Show validation issues if flagged */}
                {isFlaggedEntry && entryToDeleteData && (
                  <div className="mt-4 text-left bg-red-500/10 border border-red-400/30 rounded-lg p-3">
                    <div className="text-xs font-semibold text-red-400 mb-2">Validation Issues:</div>
                    <ul className="text-xs text-red-300 space-y-1">
                      {(entryToDeleteData.suspiciousReasons || []).slice(0, 3).map((reason, idx) => (
                        <li key={idx} className="flex items-start gap-1.5">
                          <span className="text-red-400">•</span>
                          <span>{reason}</span>
                        </li>
                      ))}
                      {(entryToDeleteData.validationIssues || []).slice(0, 3).map((issue, idx) => (
                        <li key={`issue-${idx}`} className="flex items-start gap-1.5">
                          <span className="text-red-400">•</span>
                          <span>{issue.reason || issue.type}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="btn-admin-outline btn-admin-md"
                  onClick={handleCloseDeleteModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2.5 rounded-[var(--admin-card-radius)] font-semibold text-sm bg-red-600 hover:bg-red-500 text-white"
                  onClick={handleDelete}
                >
                  {isFlaggedEntry ? 'Delete Flagged Score' : 'Delete Participant'}
                </button>
              </div>
            </div>
          </ModalBasic>
        );
      })()}
    </motion.div>
  );
};

export default ManageLeaderboard; 
